import { Languages, type LanguageT } from '~/localization/i18n';

/**
 * The journey from first contact to first session, defined once.
 *
 * This exists because it used to be written twice — a six-step version in the
 * services page copy and a seven-step version in the intake video — and the two
 * had drifted into telling different stories. The services copy quietly folded
 * the two insurance waits into one and omitted therapist matching entirely,
 * which is most of why a family reaching week five felt misled.
 *
 * Both surfaces now render from this array: the enrollment hub shows every stage
 * in full, the services page shows the same stages in short form and links to
 * the hub. They cannot disagree again without someone editing this file.
 *
 * Stages 1-2 happen before a file is open; the video covers stages 3-9.
 */

export type JourneyPhase = 'contact' | 'file';

/** Who actually sets the pace of a stage. Named because it is the honest answer. */
export type JourneyOwner = 'family' | 'bhfield' | 'insurance';

export type JourneyStage = {
  id: string;
  number: number;
  phase: JourneyPhase;
  title: string;
  /** Null where no honest estimate exists — stage 3 is bounded only by the family. */
  duration: string | null;
  owner: JourneyOwner;
  /** One sentence. Used verbatim as the short answer on the hub and on /services. */
  summary: string;
  /** Expanded detail, hub only. */
  detail: string[];
};

export const JOURNEY_PHASE_LABEL: Record<LanguageT, Record<JourneyPhase, string>> = {
  [Languages.EN]: { contact: 'Getting in touch', file: 'From your documents to your first session' },
  [Languages.ES]: { contact: 'Primer contacto', file: 'De sus documentos a su primera sesión' },
};

export const JOURNEY_OWNER_LABEL: Record<LanguageT, Record<JourneyOwner, string>> = {
  [Languages.EN]: { family: 'Your part', bhfield: 'Our part', insurance: "Your insurer's clock" },
  [Languages.ES]: { family: 'Su parte', bhfield: 'Nuestra parte', insurance: 'El reloj de su seguro' },
};

/**
 * The headline range, and the factors that decide where a family lands in it.
 *
 * The spread is wide because the two insurance approvals (stages 5 and 8) can
 * each come back in a day or take a month, and matching (stage 9) depends on
 * the family's schedule, preferences and location. The bounds are the per-stage
 * minimums and maximums summed (about 2.4 and 15.6 weeks), so the page cannot be
 * added up against itself. Stage 3 is excluded: however long a family takes to
 * gather paperwork adds on top. The range is never published without the factors
 * attached. The caveat is a lowercase clause because llms.txt splices it into a
 * sentence after the range.
 */
export const JOURNEY_TOTAL: Record<LanguageT, { range: string; from: string; caveat: string }> = {
  [Languages.EN]: {
    range: '2 to 16 weeks',
    from: 'from your first call to your first session',
    caveat:
      'depending mostly on your insurer, which can approve an authorization in 24 hours or take up to a month, and on how flexible your schedule is, any language or therapist gender preference, and your location',
  },
  [Languages.ES]: {
    range: '2 a 16 semanas',
    from: 'desde su primera llamada hasta su primera sesión',
    caveat:
      'según, sobre todo, su aseguradora, que puede aprobar una autorización en 24 horas o tardar hasta un mes, y según la flexibilidad de su horario, cualquier preferencia de idioma o de género del terapeuta, y su ubicación',
  },
};

const EN: JourneyStage[] = [
  {
    id: 'questionnaire',
    number: 1,
    phase: 'contact',
    title: 'You complete the questionnaire',
    duration: null,
    owner: 'family',
    summary: 'You tell us about your child through the questionnaire on this site.',
    detail: [
      'The questionnaire asks about your child, your concerns and what you are hoping therapy will change. It is the fastest way to reach us and it is what opens your file.',
    ],
  },
  {
    id: 'contact',
    number: 2,
    phase: 'contact',
    title: 'We contact you',
    duration: 'Within 24 hours',
    owner: 'bhfield',
    summary: 'A BH Field representative reaches out within 24 hours to talk through next steps.',
    detail: [
      'We call to understand what you need, answer your questions, and tell you exactly which documents to send. From this point you have a named contact rather than a general inbox.',
    ],
  },
  {
    id: 'documents',
    number: 3,
    phase: 'file',
    title: 'You send your documents',
    duration: null,
    owner: 'family',
    summary: 'This stage is entirely yours, and it sets the pace of everything after it.',
    detail: [
      'We cannot start until your file is complete, because insurance rejects an incomplete submission and that sends us back to the beginning.',
      'Four things make this fast: send everything at once rather than piece by piece; photographs are fine as long as all four corners are readable; ask us if you are unsure whether something counts; and tell us early if a document is hard to get, because we will gladly help.',
    ],
  },
  {
    id: 'package',
    number: 4,
    phase: 'file',
    title: 'We build your intake package',
    duration: '1 to 3 business days',
    owner: 'bhfield',
    summary: 'Our intake team verifies your benefits and prepares the authorization request.',
    detail: [
      'We verify your insurance benefits and coverage, confirm your diagnosis paperwork will be accepted, check that every form is signed and current, and prepare the authorization request.',
      'You are also introduced to your Case Manager, who becomes your named contact from that point forward.',
    ],
  },
  {
    id: 'assessment-authorization',
    number: 5,
    phase: 'file',
    title: 'Insurance approves the assessment',
    duration: '1 day to 4 weeks',
    owner: 'insurance',
    summary: 'The first of two insurance waits. Plans have up to thirty days to respond.',
    detail: [
      'Your insurer reviews the request and confirms medical necessity, verifies that your plan covers ABA and checks your benefit limits, then issues an authorization number with a set number of assessment hours.',
      'Some insurers answer within a day; others take the full four weeks. Which one you get depends entirely on your plan and is completely out of our control.',
      'We follow up on a schedule rather than waiting passively. Once a request is submitted the timeline belongs to your plan, and calling daily does not move it faster.',
    ],
  },
  {
    id: 'assessment',
    number: 6,
    phase: 'file',
    title: 'Your BCBA assesses and writes the report',
    duration: '1 to 2 weeks',
    owner: 'bhfield',
    summary: 'You meet your intake BCBA, usually across one or two visits.',
    detail: [
      'They observe and play with your child, interview you about your priorities, run standardized assessment tools, and write a full report with proposed goals.',
      'Your treatment BCBA may turn out to be a different person from the one who assesses your child.',
    ],
  },
  {
    id: 'clinical-review',
    number: 7,
    phase: 'file',
    title: 'Clinical review',
    duration: '2 to 4 business days',
    owner: 'bhfield',
    summary: 'Every report gets an internal review before your insurer sees it.',
    detail: ['A rejected plan costs weeks, so we would rather spend these days here than lose that time later.'],
  },
  {
    id: 'treatment-authorization',
    number: 8,
    phase: 'file',
    title: 'Insurance approves treatment',
    duration: '1 day to 4 weeks',
    owner: 'insurance',
    summary: 'The last major wait, and the decision that sets your hours.',
    detail: [
      'Your insurer decides whether ongoing ABA is approved, how many therapy hours per week they will authorize, how long the authorization lasts before renewal, and how many parent support hours are included.',
      'As with the first approval, this can take as little as a day or as long as four weeks depending on your insurer, and the timing is completely out of our control.',
      'Approved hours sometimes differ from what your BCBA recommended. If that happens we will explain what it means, and we can appeal.',
      'Authorizations expire, so every few months we resubmit to continue services.',
    ],
  },
  {
    id: 'matching',
    number: 9,
    phase: 'file',
    title: 'We match your team',
    duration: '1 business day to 4 weeks',
    owner: 'bhfield',
    summary: 'Approval is not the same as a start date — we still need the right therapist for your family.',
    detail: [
      'Once your hours are approved we look for an RBT whose availability, location and skills fit your family. Your schedule has to overlap with a therapist’s, so flexible availability is matched faster. In-home services depend on a provider who covers your area.',
      'Language and provider gender requests are honored where we can, though some take longer to fill. If we cannot staff your case quickly, we will tell you honestly rather than leave you wondering.',
      'Before therapy begins there is a short virtual kick-off call with you, your BCBA, your Case Manager and your RBT, to confirm the schedule and go through your child’s routines, sensitivities and safety notes.',
    ],
  },
];

const ES: JourneyStage[] = [
  {
    id: 'questionnaire',
    number: 1,
    phase: 'contact',
    title: 'Usted completa el cuestionario',
    duration: null,
    owner: 'family',
    summary: 'Nos cuenta sobre su hijo a través del cuestionario de este sitio.',
    detail: [
      'El cuestionario pregunta por su hijo, sus preocupaciones y qué espera que cambie la terapia. Es la forma más rápida de contactarnos y es lo que abre su expediente.',
    ],
  },
  {
    id: 'contact',
    number: 2,
    phase: 'contact',
    title: 'Nos comunicamos con usted',
    duration: 'En 24 horas',
    owner: 'bhfield',
    summary: 'Un representante de BH Field se comunica en un plazo de 24 horas para explicar los siguientes pasos.',
    detail: [
      'Llamamos para entender lo que necesita, responder sus preguntas y decirle exactamente qué documentos enviar. Desde ese momento tiene un contacto con nombre y apellido, no un buzón general.',
    ],
  },
  {
    id: 'documents',
    number: 3,
    phase: 'file',
    title: 'Usted envía sus documentos',
    duration: null,
    owner: 'family',
    summary: 'Esta etapa es enteramente suya y marca el ritmo de todo lo que sigue.',
    detail: [
      'No podemos comenzar hasta que su expediente esté completo, porque el seguro rechaza un envío incompleto y eso nos devuelve al principio.',
      'Cuatro cosas lo agilizan: envíe todo junto y no por partes; las fotografías sirven siempre que se lean las cuatro esquinas; pregúntenos si no está seguro de si algo cuenta; y avísenos pronto si un documento es difícil de conseguir, porque con gusto le ayudamos.',
    ],
  },
  {
    id: 'package',
    number: 4,
    phase: 'file',
    title: 'Preparamos su paquete de admisión',
    duration: '1 a 3 días hábiles',
    owner: 'bhfield',
    summary: 'Nuestro equipo de admisión verifica sus beneficios y prepara la solicitud de autorización.',
    detail: [
      'Verificamos los beneficios y la cobertura de su seguro, confirmamos que la documentación del diagnóstico será aceptada, revisamos que todos los formularios estén firmados y vigentes, y preparamos la solicitud de autorización.',
      'También le presentamos a su Case Manager, que será su contacto designado a partir de ese momento.',
    ],
  },
  {
    id: 'assessment-authorization',
    number: 5,
    phase: 'file',
    title: 'El seguro aprueba la evaluación',
    duration: '1 día a 4 semanas',
    owner: 'insurance',
    summary: 'La primera de dos esperas del seguro. Los planes tienen hasta treinta días para responder.',
    detail: [
      'Su aseguradora revisa la solicitud y confirma la necesidad médica, verifica que su plan cubra ABA y revisa los límites de su beneficio, y luego emite un número de autorización con una cantidad determinada de horas de evaluación.',
      'Algunas aseguradoras responden en un día; otras tardan las cuatro semanas completas. Cuál le toque depende enteramente de su plan y está completamente fuera de nuestro control.',
      'Hacemos seguimiento de forma programada en lugar de esperar pasivamente. Una vez enviada la solicitud, el calendario pertenece a su plan, y llamar todos los días no lo acelera.',
    ],
  },
  {
    id: 'assessment',
    number: 6,
    phase: 'file',
    title: 'Su BCBA evalúa y redacta el informe',
    duration: '1 a 2 semanas',
    owner: 'bhfield',
    summary: 'Conoce a su BCBA de admisión, normalmente en una o dos visitas.',
    detail: [
      'Observa y juega con su hijo, le entrevista sobre sus prioridades, aplica herramientas de evaluación estandarizadas y redacta un informe completo con los objetivos propuestos.',
      'Su BCBA de tratamiento puede ser una persona distinta de quien realiza la evaluación.',
    ],
  },
  {
    id: 'clinical-review',
    number: 7,
    phase: 'file',
    title: 'Revisión clínica',
    duration: '2 a 4 días hábiles',
    owner: 'bhfield',
    summary: 'Cada informe pasa por una revisión interna antes de que lo vea su aseguradora.',
    detail: [
      'Un plan rechazado cuesta semanas, así que preferimos dedicar estos días aquí antes que perder ese tiempo después.',
    ],
  },
  {
    id: 'treatment-authorization',
    number: 8,
    phase: 'file',
    title: 'El seguro aprueba el tratamiento',
    duration: '1 día a 4 semanas',
    owner: 'insurance',
    summary: 'La última espera importante, y la decisión que define sus horas.',
    detail: [
      'Su aseguradora decide si aprueba la terapia ABA continua, cuántas horas de terapia por semana autoriza, cuánto dura la autorización antes de renovarse y cuántas horas de apoyo a los padres se incluyen.',
      'Igual que con la primera aprobación, puede tardar desde un día hasta cuatro semanas según su aseguradora, y ese plazo está completamente fuera de nuestro control.',
      'A veces las horas aprobadas difieren de las que recomendó su BCBA. Si ocurre, le explicaremos qué significa y podemos apelar.',
      'Las autorizaciones vencen, así que cada pocos meses volvemos a presentarlas para continuar los servicios.',
    ],
  },
  {
    id: 'matching',
    number: 9,
    phase: 'file',
    title: 'Formamos su equipo',
    duration: '1 día hábil a 4 semanas',
    owner: 'bhfield',
    summary: 'La aprobación no es lo mismo que una fecha de inicio: aún hay que encontrar al terapeuta adecuado.',
    detail: [
      'Una vez aprobadas sus horas, buscamos un RBT cuya disponibilidad, ubicación y habilidades se ajusten a su familia. Su horario debe coincidir con el de un terapeuta, así que la disponibilidad flexible se asigna más rápido. Los servicios en casa dependen de que haya un proveedor que cubra su zona.',
      'Atendemos las solicitudes de idioma y de género del proveedor siempre que podemos, aunque algunas tardan más en cubrirse. Si no podemos asignar su caso con rapidez, se lo diremos con honestidad en lugar de dejarle esperando.',
      'Antes de comenzar la terapia hay una breve reunión virtual inicial con usted, su BCBA, su Case Manager y su RBT, para confirmar el horario y repasar las rutinas, sensibilidades y notas de seguridad de su hijo.',
    ],
  },
];

export const INTAKE_JOURNEY: Record<LanguageT, JourneyStage[]> = {
  [Languages.EN]: EN,
  [Languages.ES]: ES,
};

export function journeyFor(locale: string): JourneyStage[] {
  return INTAKE_JOURNEY[locale === Languages.ES ? Languages.ES : Languages.EN];
}
